import { redirectToDashboardOnSmallScreens } from '@core/js/utils/loginUtils';

/**
 * Asynchronously load loyalty bundle at runtime
 * @param  {Object} e             Event from event handler that triggered the quickshop launch
 * @param  {Array}  componentName name of component
 * @return {Promise}              Promise for clients to chain logic that should only
 *                                happen after loyalty modal has launched
 */
export function launchAsyncLoyaltyModal(e, componentName, transitionType, showSuccess) {
    return import('@core/js/utils/async/loyaltyLoader').then(({
        replaceModalTransition,
        pushModalTransition,
        loyaltyModalInit
    }) => {
        let payload;

        if (transitionType === 'replace') {
            payload = replaceModalTransition(loyaltyModalInit, { showSuccess }, e, componentName);
        } else {
            payload = pushModalTransition(loyaltyModalInit, { showSuccess }, e, componentName);
        }

        $.event.trigger({
            type: payload.type,
            payload: payload
        });
    });
}

/**
 * Asynchronously load login bundle at runtime
 * @param  {Object} e             Event from event handler that triggered the login modal
 * @param  {Array}  componentName name of component
 * @return {Promise}              Promise for clients to chain logic that should only
 *                                happen after login modal has launched
 */
export function launchAsyncLoginModal(e, componentName) {
    return import('@core/js/utils/async/loginLoader').then(({
        replaceModalTransition,
        loginInit
    }) => {
        const payload = replaceModalTransition(loginInit, {}, e, componentName);
        $.event.trigger({
            type: payload.type,
            payload: payload
        });
    });
}

/**
 * Asynchronously load sign in bundle at runtime
 * @param  {Object} e             Event from event handler that triggered the sign in modal
 * @param  {Array}  componentName name of component
 * @param  {boolean} redirect     Flag to indicate whether we should redirect after login
 * @return {Promise}              Promise for clients to chain logic that should
 *                                only happen after sign in modal has launched
 */
export function launchAsyncSignInModal(e, componentName, redirect = true) {
    return import('@core/js/utils/async/signinLoader').then(({
        pushModalTransition,
        loginInit
    }) => {
        const config = redirect ? { finalCloseAction: redirectToDashboardOnSmallScreens } : {};
        const payload = pushModalTransition(loginInit, config, e, componentName);

        $.event.trigger({
            type: payload.type,
            payload: payload
        });
    });
}

/**
 * Asynchronously load create user bundle at runtime
 * @param  {Object} e             Event from event handler that triggered the create user modal
 * @param  {Array}  componentName name of component
 * @return {Promise}              Promise for clients to chain logic that should only
 *                                happen after create user modal has launched
 */
export function launchAsyncCreateUserModal(e, componentName) {
    return import('@core/js/utils/async/createUserLoader').then(({
        replaceModalTransition,
        signupInit
    }) => {
        const payload = replaceModalTransition(signupInit, {}, e, componentName);
        $.event.trigger({
            type: payload.type,
            payload: payload
        });
    });
}

/**
 * Asynchronously load email sign up bundle at runtime.
 * @param {Object} e Event from event handler that triggered the create user modal
 * @param {Array} componentName Name of component.
 * @returns {Promise} Promise for clients to chain logic that should only
 *                    happen after email sign up modal has launched.
 */
export function launchAsyncEmailSignUpModal(e, componentName, storeLocation = '', optInSource = '') {
    return import('@core/js/utils/async/emailSignUpLoader').then(({
        pushModalTransition,
        emailSignUpInit
    }) => {

        const emailSignupOverrides = {
            originatedFromOutsideAccount: true,
            storeLocation,
            optInSource,
        };

        const payload = pushModalTransition(emailSignUpInit, emailSignupOverrides, e, componentName);
        $.event.trigger({
            type: payload.type,
            payload: payload
        });
    });
}

/**
 * Asynchronously load collection points bundle at runtime.
 * @param {Object} e            Event from event handler that triggered the collection points modal
 * @param {Array} componentName Name of component.
 * @returns {Promise}           Promise for clients to chain logic that should only
 *                              happen after collection points modal has launched.
 */
export function launchCollectionPointsInfoModal(e, componentName) {
    return import('@core/js/utils/async/collectionPointsInfoLoader').then(({
        pushModalTransition,
        collectionPointsInfoInit
    }) => {
        const payload = pushModalTransition(collectionPointsInfoInit, {}, e, componentName);
        $.event.trigger({
            type: payload.type,
            payload: payload
        });
    });
}

/**
 * Asynchronously load collection points bundle at runtime.
 * @param {Object} e            Event from event handler that triggered the collection points modal
 * @param {Array} componentName Name of component.
 * @returns {Promise}           Promise for clients to chain logic that should only
 *                              happen after collection points modal has launched.
 */
export function launchBopisRestrictedModal(e, componentName) {
    return import('@core/js/utils/async/bopisRestrictedLoader').then(({
        pushModalTransition,
        bopisRestrictedModalInit
    }) => {

        const payload = pushModalTransition(bopisRestrictedModalInit, {}, e, componentName);
        $.event.trigger({
            type: payload.type,
            payload: payload
        });
    });
}

/**
 * Asynchronously load imports for the in-store-pick-up modal
 * @param {Object} e            Event object from event handler (triggered by SlipStream)
 * @param {Array} componentName Name of component.
 * @returns {Promise}           Promise for clients to chain logic that should only
 *                              happen after the modal has launched.
 */
export function launchISPUInfoModal(e, componentName) {
    return import('@core/js/utils/async/ispuInfoLoader').then(({
        pushModalTransition,
        ispuInfoInit,
    }) => {

        const payload = pushModalTransition(ispuInfoInit, {}, e, componentName);
        $.event.trigger({
            type: payload.type,
            payload: payload
        });
    });
}

/**
 * Asynchronously load quickship bundle at runtime.
 * @param {Object} e            Event from event handler that triggered the quickship info modal
 * @param {Array} componentName Name of component.
 * @returns {Promise}           Promise for clients to chain logic that should only
 *                              happen after the modal has launched.
 */
export function launchQuickshipInfoModal(e, componentName) {
    return import('@core/js/utils/async/quickshipInfoLoader').then(({
        pushModalTransition,
        quickshipInfoModalInit
    }) => {
        const payload = pushModalTransition(quickshipInfoModalInit, {}, e, componentName);
        $.event.trigger({
            type: payload.type,
            payload: payload
        });
    });
}

export function launchSiteSwitchModal(e, componentName, overrides = {}) {
    return import('@core/js/utils/async/siteSwitchModalLoader').then(({
        pushModalTransition,
        siteSwitchModalInit
    }) => {
        const payload = pushModalTransition(siteSwitchModalInit, overrides, e, componentName);

        $.event.trigger({
            type: payload.type,
            payload: payload
        });
    });
}
